import {
  Arrange,
  Container,
  Icon,
  Spacer,
  SvgChevronSmallDown,
  SvgPresent,
  SvgSparkle,
  SvgUsersAdd,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import classNames from 'classnames';
import React from 'react';
import { Arrow, useLayer } from 'react-laag';
import { useDispatch, useSelector } from 'react-redux';

import { showInviteTeammatesToolTip } from 'actions/layout';
import { openModal } from 'actions/modals';
import { STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED } from 'constants/events';
import { EXP_INVITE_STORAGE_INCENTIVES } from 'constants/featureFlag';
import { TEAM_INVITE_MODAL } from 'constants/modalTypes';
import {
  INVITE_ADMIN_ACTION,
  INVITE_CREATOR_ACTION,
  INVITE_CREATOR_LITE_ACTION,
  INVITE_VIEWER_ACTION,
} from 'constants/scopes';
import * as analytics from 'utilities/analytics';
import { useStorageIncentivesFTUXButtonState } from 'utilities/localStorage/ftux';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { useHasSomeScopes } from 'hooks/useHasScopes';
import { useMatchMobileOnly } from 'hooks/useMatchMedia';

import Scopes from 'components/scopes';
import { INVITE_TEAMMATES_DROPDOWN_HEADER } from 'components/storage-incentives/constants';
import WorkspaceLogo from 'components/workspace-logo';

import styles from './styles.module.less';

const inviteScopes = [
  INVITE_VIEWER_ACTION,
  INVITE_CREATOR_ACTION,
  INVITE_CREATOR_LITE_ACTION,
  INVITE_ADMIN_ACTION,
];

const inviteButtonContent = {
  'invite-teammates': {
    title: 'Invite teammates',
    icon: <SvgUsersAdd />,
    theme: 'themePrimary',
  },
  'storage-incentives': {
    title: 'Earn free videos',
    icon: <SvgPresent />,
    theme: 'themeTealDark',
  },
  'storage-incentives-after-ftux': {
    title: 'Invite teammates',
    icon: <SvgPresent />,
    theme: 'themeTealDark',
  },
};

export default function DropdownHeader({
  toggleDropdown,
  selectedWorkspace,
  isCompact,
  showSuggestedWorkspaceText,
}) {
  const dispatch = useDispatch();
  const isMobile = useMatchMobileOnly();
  const hasInviteScope = useHasSomeScopes(inviteScopes);
  const buttonId = 'workspaceDropdownHeader';
  const onClick = () => toggleDropdown(open => !open);
  const { name, members } = selectedWorkspace;
  const memberText = members === 1 ? '1 member' : `${members} members`;
  const suggestedWorkspaceText = (
    <Arrange gap="xsmall">
      <Text color="blurple" size="small" fontWeight="bold" isInline>
        New Workspace to join
      </Text>
      <Icon size={2} color="blurple" icon={<SvgSparkle />} />
    </Arrange>
  );
  const description = showSuggestedWorkspaceText
    ? suggestedWorkspaceText
    : memberText;

  const showInviteToolTip = useSelector(
    state => state.layout.showInviteToolTip
  );

  const showStorageIncentives = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES,
    activationValues: ['variant'],
  });

  const [
    hasDismissedStorageIncentivesFtuxButton,
    dismissedStorageIncentivesFtuxButton,
  ] = useStorageIncentivesFTUXButtonState();

  let content;

  if (showStorageIncentives && !hasDismissedStorageIncentivesFtuxButton) {
    content = inviteButtonContent['storage-incentives'];
  } else if (showStorageIncentives) {
    content = inviteButtonContent['storage-incentives-after-ftux'];
  } else {
    content = inviteButtonContent['invite-teammates'];
  }

  const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
    isOpen: !isMobile && showInviteToolTip,
    placement: 'right-start',
    triggerOffset: 24,
  });

  return (
    <div className="relative">
      <Tooltip isInline={false} content={isCompact ? 'Switch Workspace' : null}>
        <button
          id={buttonId}
          className={classNames(
            styles.workspaceButton,
            !hasInviteScope && styles.roundBottom
          )}
          onClick={onClick}
        >
          <div className={styles.workspaceContentCompact}>
            <Arrange width={6} height={6} justifyContent="center">
              <WorkspaceLogo workspace={selectedWorkspace} />
            </Arrange>
          </div>
          <div className={styles.workspaceContentNormal}>
            <Arrange columns={['1fr', '1.5rem']} width="100%">
              <span className="width:full">
                <Text
                  alignment="left"
                  fontWeight="bold"
                  className={styles.workspaceName}
                  hasEllipsis
                >
                  {name}
                </Text>
                <Text alignment="left" color="bodyDimmed" size="small">
                  {description}
                </Text>
              </span>
              <Icon icon={<SvgChevronSmallDown />} />
            </Arrange>
          </div>
        </button>
      </Tooltip>
      <Scopes names={inviteScopes} some={true}>
        <Tooltip content={isCompact ? content.title : null} isInline={false}>
          <button
            id="navigationInviteTeammatesButton"
            onClick={() => {
              dispatch(openModal(TEAM_INVITE_MODAL));
              dismissedStorageIncentivesFtuxButton();

              analytics.track(STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED, {
                source: INVITE_TEAMMATES_DROPDOWN_HEADER,
                path: window.location.pathname,
                content: content.title,
              });
            }}
            className={classNames(
              styles.inviteButton,
              isCompact && styles.isCompact,
              styles[content.theme]
            )}
            {...triggerProps}
          >
            <span className={styles.inviteButtonContent}>
              <span className={styles.inviteIcon}>
                <Icon color="currentColor" icon={content.icon} />
              </span>
              <Spacer right="small" />
              <Text
                color="currentColor"
                size="small"
                fontWeight="medium"
                className={styles.inviteButtonLabel}
              >
                {content.title}
              </Text>
            </span>
          </button>
        </Tooltip>
      </Scopes>
      {!isMobile &&
        showInviteToolTip &&
        renderLayer(
          <div
            className={classNames(styles.banner, 'theme-dark')}
            {...layerProps}
          >
            <Container
              backgroundColor="grey7"
              padding="medium"
              radius="medium"
              width="12rem"
            >
              <span className={styles.bannerArrow} />
              <Text color="body" fontWeight="medium">
                Click here to invite your teammates at any time.
              </Text>
              <Spacer top="small" />
              <TextButton
                onClick={() => dispatch(showInviteTeammatesToolTip(false))}
                size="small"
                offsetSide="left"
              >
                Dismiss
              </TextButton>
            </Container>
            <Arrow
              {...arrowProps}
              backgroundColor="var(--lns-color-grey7)"
              roundness={1}
            />
          </div>
        )}
    </div>
  );
}
